import type { TravelerResponseDto, UpdateTravelerDto } from '@bandido/shared/dtos';
import type { RuteroPassengerPayload } from './queries';

export const IDENTITY_DOCUMENT_TYPES = ['passport', 'dni', 'ci'] as const;
export type IdentityDocumentType = (typeof IDENTITY_DOCUMENT_TYPES)[number];

// Matches TravelersService.requiredText, the persisted display-number normalization.
const persistedDocumentNumber = (value?: string) => value?.trim().replace(/\s+/g, ' ');

export function identityDocumentNeedsSelection(
  passenger: RuteroPassengerPayload,
  traveler?: TravelerResponseDto | null,
): boolean {
  const selection = passenger.identityDocument;
  return Boolean(selection && (selection.unresolved || (selection.id && traveler &&
    !traveler.documents.some((document) => document.id === selection.id && document.type.toLowerCase() === selection.type))));
}

/** A successful write replaces document IDs. Correlate its exact submitted
 * document with the response, never with a later edited booking number. */
export function reconcilePersistedIdentityDocument(
  passenger: RuteroPassengerPayload,
  submitted: RuteroPassengerPayload,
  traveler: TravelerResponseDto,
): RuteroPassengerPayload {
  const selection = submitted.identityDocument;
  if (!selection || passenger.identityDocument?.type !== selection.type ||
    passenger.identityDocument?.id !== selection.id) return passenger;
  const retained = traveler.documents.find((document) => document.id === selection.id &&
    document.type.toLowerCase() === selection.type);
  if (retained) {
    return { ...passenger, identityDocument: { type: selection.type, id: retained.id } };
  }
  const matches = traveler.documents.filter((document) =>
    document.type.toLowerCase() === selection.type && document.number === persistedDocumentNumber(submitted.passportNumber));
  return {
    ...passenger,
    identityDocument: matches.length === 1
      ? { type: selection.type, id: matches[0].id }
      : { ...selection, unresolved: true },
  };
}

export function reconcileSavedDocumentDraft(
  passengers: RuteroPassengerPayload[],
  submitted: RuteroPassengerPayload,
  traveler: TravelerResponseDto,
  expectedDraft: number,
  currentDraft: number,
): RuteroPassengerPayload[] {
  if (expectedDraft !== currentDraft) return passengers;
  return passengers.map((passenger) => passenger.travelerId === traveler.id
    ? reconcilePersistedIdentityDocument(passenger, submitted, traveler) : passenger);
}

export function identityDocuments(traveler?: TravelerResponseDto | null) {
  return (traveler?.documents ?? []).filter((document) =>
    IDENTITY_DOCUMENT_TYPES.some((type) => type === document.type.toLowerCase()),);
}

/** Multiple documents require an explicit choice; never prefer a passport. */
export function transferDocumentFromTraveler(
  passenger: RuteroPassengerPayload,
  traveler: TravelerResponseDto,
): RuteroPassengerPayload {
  const documents = identityDocuments(traveler);
  const document = documents.length === 1 ? documents[0] : undefined;
  return {
    ...passenger,
    identityDocument: document
      ? { id: document.id, type: document.type.toLowerCase() as IdentityDocumentType }
      : undefined,
    passportNumber: document?.number,
    passportExpiry: undefined,
  };
}

/** Update only the selected document; never infer its identity from its number. */
export function identityDocumentPatch(
  passenger: RuteroPassengerPayload,
  traveler: TravelerResponseDto,
): UpdateTravelerDto['documents'] | undefined {
  const selection = passenger.identityDocument;
  const number = persistedDocumentNumber(passenger.passportNumber);
  if (!selection || !number || selection.unresolved) return undefined;
  const selected = selection.id
    ? traveler.documents.find((document) => document.id === selection.id &&
      document.type.toLowerCase() === selection.type)
    : undefined;
  if (selection.id && !selected) return undefined;
  const expiry = selection.type === 'passport' ? passenger.passportExpiry : undefined;
  if (selected?.number === number && (expiry === undefined || expiry === (selected.expiresOn ?? ''))) return undefined;
  // A previously completed create may already have persisted this new document.
  if (!selection.id && traveler.documents.some((document) =>
    document.type.toLowerCase() === selection.type && document.number === number &&
    (expiry === undefined || expiry === (document.expiresOn ?? '')))) return undefined;
  const documents = traveler.documents.map((document) => {
    const expiresOn = document.id === selected?.id && expiry !== undefined
      ? expiry : document.expiresOn;
    return {
      type: document.type,
      number: document.id === selected?.id ? number : document.number,
      ...(document.issuingCountry ? { issuingCountry: document.issuingCountry } : {}),
      ...(expiresOn ? { expiresOn } : {}),
    };
  });
  if (!selected) documents.push({ type: selection.type, number, ...(expiry ? { expiresOn: expiry } : {}) });
  return documents;
}
